import React, { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import FromField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/FromField'
import StopField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/StopField'
import ToField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/ToField'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'

import { useMileageDistanceCalculation } from './useMileageDistanceCalculation'

interface Props extends PermissionsProps, CustomizationProps {
  setIsCalculatingDistance?: (loading: boolean) => void
}

interface Place {
  lat: number
  lng: number
}

interface StopFormValue {
  name?: string | null
  latitude?: string | null
  longitude?: string | null
}

const Trip = (props: Props) => {
  const { setValue, watch } = useFormContext()
  const { setIsCalculatingDistance } = props

  const hasExistingData = Boolean(props.expensable?.id)

  const { stops } = useMileageDistanceCalculation({
    setIsCalculatingDistance,
    hasExistingData,
  })

  const handleFromPlaceSelected = useCallback(
    (place: Place) => {
      setValue('originLatitude', String(place.lat), { shouldValidate: true, shouldDirty: true })
      setValue('originLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleToPlaceSelected = useCallback(
    (place: Place) => {
      setValue('destinationLatitude', String(place.lat), {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue('destinationLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleStopPlaceSelected = useCallback(
    (index: number, place: Place) => {
      setValue(`stops.${index}.latitude`, String(place.lat), {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue(`stops.${index}.longitude`, String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleAddStop = useCallback(() => {
    const current: StopFormValue[] = watch('stops') || []
    const next = [...current, { name: '', latitude: null, longitude: null }]
    setValue('stops', next, { shouldDirty: true })
  }, [watch, setValue])

  const handleRemoveStop = useCallback(
    (index: number) => {
      const current: StopFormValue[] = watch('stops') || []
      const next = current.filter((_, i) => i !== index)
      setValue('stops', next, { shouldDirty: true })
    },
    [watch, setValue]
  )

  return (
    <Box gap="s16">
      <FromField {...props} fieldIdentifier="from" onPlaceSelected={handleFromPlaceSelected} />

      {stops.map((_stop: StopFormValue, index: number) => (
        <Box key={index} flexDirection="row" gap="s8" alignItems="center" width="full">
          <Box flexGrow width="full">
            <StopField
              {...props}
              fieldIdentifier="stops"
              index={index}
              onPlaceSelected={(place) => handleStopPlaceSelected(index, place)}
            />
          </Box>
          <Box paddingTop="s24">
            <Button type="secondary" onClick={() => handleRemoveStop(index)} iconName="delete" />
          </Box>
        </Box>
      ))}

      <Button type="secondary" onClick={handleAddStop}>
        {i18n.t('expenses_v2.forms.fields.stop.add')}
      </Button>

      <ToField {...props} fieldIdentifier="to" onPlaceSelected={handleToPlaceSelected} />
    </Box>
  )
}

export default Trip
